import axios from "axios";
import { geometryToWsg84 } from "../utils/road";

const VAYLA_BASE = "https://avoinapi.vaylapilvi.fi/viitekehysmuunnin/muunna";

/** The return values the road geometry lookup needs (katunimi, geometry, length, …). */
const PALAUTUSARVOT = [1, 2, 3, 4, 5, 6]
  .map((n) => `palautusarvot=${n}`)
  .join("&");

/**
 * A miss is not an HTTP error: the service answers 200 with a single feature
 * whose only property is `virheet` and whose geometry has no coordinates.
 * Everything that talks to this API has to check for it.
 */
export const roadLookupError = (data: any): string | null => {
  const virheet = data?.features?.[0]?.properties?.virheet;
  return typeof virheet === "string" && virheet.trim().length > 0
    ? virheet.trim()
    : null;
};

export const getRoadInfo = async (streetName: string, municipality: string) => {
  const vaylaUrl =
    `${VAYLA_BASE}?kuntanimi=${encodeURIComponent(municipality)}` +
    `&katunimi=${encodeURIComponent(
      streetName
    )}&valihaku=true&${PALAUTUSARVOT}`;
  const road = await axios.get(vaylaUrl);
  return road;
};

export interface NearestRoad {
  name: string;
  municipality: string;
  /** Distance in metres from the queried point to the road it snapped to. */
  distanceM: number;
}

/** A road link the register knows about but has no name for. */
export interface UnnamedRoadLink {
  linkId: string;
  municipality: string | null;
  distanceM: number;
}

export type NearestRoadResult =
  | { kind: "named"; road: NearestRoad }
  | { kind: "unnamed"; link: UnnamedRoadLink }
  | { kind: "none" };

export const getRoadInfoFromXY = async (x: number, y: number) => {
  const vaylaUrl = `${VAYLA_BASE}?x=${x}&y=${y}&${PALAUTUSARVOT}`;
  const road = await axios.get(vaylaUrl);
  return road;
};

/**
 * Snap an ETRS-TM35FIN coordinate to the nearest road in the national road
 * register.
 *
 * Three outcomes, not two. A named hit gives the canonical spelling, and
 * feeding that back into `getRoadInfo` is what rescues a user who could not
 * spell it. But the register also returns links it has no name for — unnamed
 * private roads, which is precisely what many tiekunta own. Those used to be
 * reported as "no road here"; they are real roads whose geometry is reachable
 * by `link_id`, so they get their own outcome.
 */
export const findNearestRoadResult = async (
  easting: number,
  northing: number
): Promise<NearestRoadResult> => {
  const response = await getRoadInfoFromXY(easting, northing);

  if (roadLookupError(response.data)) return { kind: "none" };

  const properties = response.data?.features?.[0]?.properties;
  if (!properties) return { kind: "none" };

  const distanceM = Number(properties.valimatka ?? 0);

  if (properties.katunimi && properties.kuntanimi) {
    return {
      kind: "named",
      road: {
        name: properties.katunimi,
        municipality: properties.kuntanimi,
        distanceM,
      },
    };
  }

  if (properties.link_id) {
    return {
      kind: "unnamed",
      link: {
        linkId: properties.link_id,
        municipality: properties.kuntanimi ?? null,
        distanceM,
      },
    };
  }

  return { kind: "none" };
};

export interface RoadLinkGeometry {
  coordinates: number[][];
  /** The register's own measurement, preferred over recomputing from the line. */
  lengthMeters: number | null;
}

/**
 * Geometry for a single road link, in WGS84.
 *
 * This is the only way to get a line for a road the register cannot resolve by
 * name — `valihaku=true` is required, without it the same query answers with a
 * bare Point. One call returns exactly one link, so a road made of several
 * links takes one call per link.
 *
 * The register returns these coordinates in 3D (x, y, z); `geometryToWsg84`
 * drops the elevation, which is what keeps the rest of the pipeline 2D.
 */
export const getRoadGeometryByLinkId = async (
  linkId: string
): Promise<RoadLinkGeometry | null> => {
  const url =
    `${VAYLA_BASE}?link_id=${encodeURIComponent(linkId)}` +
    `&valihaku=true&${PALAUTUSARVOT}`;
  const response = await axios.get(url);

  if (roadLookupError(response.data)) return null;

  const feature = response.data?.features?.[0];
  if (!feature?.geometry || feature.geometry.type !== "LineString") return null;

  const coordinates = geometryToWsg84(feature) as number[][];
  if (coordinates.length < 2) return null;

  const length = Number(feature.properties?.viivan_pituus);
  return {
    coordinates,
    lengthMeters: Number.isFinite(length) ? Math.round(length) : null,
  };
};
